<template>
	<div
		:class="[
			$style['app-table'],
			!showFooter && $style['app-table--without-footer']
		]"
		:style="{ scrollMarginTop: stickyTopCss }"
	>
		<template v-if="slotParts().header">
			<VNodes :vnodes="slotParts().before" />
			<div
				:class="$style['app-table__sticky-header']"
				:style="headerStyle"
			>
				<div
					ref="headerTarget"
					:class="[
						$style['app-table__sticky-header-scroller'],
						{
							'is-scrolled-x': isScrolledX,
							'is-scrolled-x-end': isScrolledXEnd
						}
					]"
					@scroll.passive="onScroll('header')"
					@wheel.passive="lockScrollSource('header')"
					@pointerdown="lockScrollSource('header')"
					@touchstart.passive="lockScrollSource('header')"
				>
					<VNodes :vnodes="[slotParts().header]" />
				</div>
				<div
					v-if="$scopedSlots['sticky-overlay']"
					ref="overlayTarget"
					:class="[
						$style['app-table__sticky-overlay'],
						{
							'is-scrolled-x': isScrolledX,
							'is-scrolled-x-end': isScrolledXEnd
						}
					]"
					@scroll.passive="onOverlayScroll"
					@wheel.passive="lockScrollSource('overlay')"
					@pointerdown="lockScrollSource('overlay')"
					@touchstart.passive="lockScrollSource('overlay')"
				>
					<slot name="sticky-overlay" :content-width="contentWidth" />
				</div>
			</div>
		</template>
		<div
			ref="tableInner"
			:class="[
				$style['app-table__inner'],
				showStickyScrollbar && $style['app-table__inner--proxy-scroll'],
				{
					'is-scrolled-x': isScrolledX,
					'is-scrolled-x-end': isScrolledXEnd
				}
			]"
			:style="innerStyle"
			@scroll.passive="onScroll('inner')"
			@wheel.passive="lockScrollSource('inner')"
			@pointerdown="lockScrollSource('inner')"
			@touchstart.passive="lockScrollSource('inner')"
		>
			<VNodes v-if="slotParts().header" :vnodes="slotParts().body" />
			<slot v-else />
		</div>
		<div
			v-if="showFooter || showStickyScrollbar"
			data-app-table-sticky-footer
			:class="isPageStickyFooter && $style['app-table__sticky-footer']"
		>
			<div
				v-if="showStickyScrollbar"
				ref="scrollbarProxy"
				:class="[
					$style['app-table__sticky-scrollbar'],
					!showFooter && $style['app-table__sticky-scrollbar--last']
				]"
				@scroll.passive="onScroll('proxy')"
				@wheel.passive="lockScrollSource('proxy')"
				@pointerdown="lockScrollSource('proxy')"
				@touchstart.passive="lockScrollSource('proxy')"
			>
				<div :style="{ width: `${contentWidth}px`, height: overlayScrollbar ? '10px' : '1px' }" />
			</div>
			<BaseTableFooter
				v-if="showFooter"
				:no-grid-options="noGridOptions"
				:page="page"
				:per-page="perPage"
				:show-page-options="showPageOptions"
				:pages="pages"
				:total="total"
				:meta="meta"
				:individual="false"
				:display-type="displayType"
				@per-page-change="$emit('per-page-change', $event)"
				@page-change="handlePageChange"
				@display-type-change="$emit('display-type-change', $event)"
			/>
		</div>
	</div>
</template>
<script>
const VNodes = {
	functional: true,
	props: {
		vnodes: {
			type: Array,
			default: () => []
		}
	},
	render: (h, { props }) => props.vnodes
}

const isHeaderRowVNode = (vnode) => {
	const options = vnode && vnode.componentOptions
	if (!options || options.Ctor.options.name !== 'BaseTableRow') return false
	const header = options.propsData && options.propsData.header
	return header === '' || !!header
}

export default {
	name: 'BaseTable',
	components: {
		VNodes
	},
	inject: {
		baseTableNested: {
			default: false
		}
	},
	props: {
		showFooter: {
			type: Boolean,
			default: true
		},
		showPageOptions: {
			type: Boolean,
			default: true
		},
		noGridOptions: {
			type: [String, Boolean],
			default: false
		},
		displayType: {
			type: String,
			default: null
		},
		page: {
			type: [String, Number],
			default: 0
		},
		perPage: {
			type: [String, Number],
			default: 0
		},
		pages: {
			type: [String, Number],
			default: 0
		},
		total: {
			type: [String, Number],
			default: 0
		},
		meta: {
			type: Object,
			default: () => ({})
		},
		stickyHeader: {
			type: Boolean,
			default: true
		},
		stickyFooter: {
			type: Boolean,
			default: true
		},
		maxHeight: {
			type: String,
			default: null
		},
		stickyOffset: {
			type: [Number, String],
			default: null
		}
	},
	data () {
		return {
			isScrolledX: false,
			isScrolledXEnd: false,
			viewportWidth: 0,
			contentWidth: 0,
			hasOverflowX: false,
			overlayScrollbar: false,
			inScrollContainer: false
		}
	},
	computed: {
		showStickyScrollbar () {
			return this.hasOverflowX && !this.effectiveMaxHeight
		},
		effectiveMaxHeight () {
			return this.maxHeight || (this.baseTableNested ? '400px' : null)
		},
		isPageStickyHeader () {
			return this.stickyHeader && !this.effectiveMaxHeight
		},
		isPageStickyFooter () {
			return this.stickyFooter && !this.effectiveMaxHeight
		},
		stickyTopCss () {
			if (this.stickyOffset !== null && this.stickyOffset !== undefined && this.stickyOffset !== '') {
				return `${parseFloat(this.stickyOffset) || 0}px`
			}
			return this.inScrollContainer ? '0px' : 'var(--app-header-height, 0px)'
		},
		viewportWidthStyle () {
			return this.viewportWidth ? { '--table-viewport-width': `${this.viewportWidth}px` } : {}
		},
		innerStyle () {
			return {
				...(this.stickyHeader && this.effectiveMaxHeight
					? { maxHeight: this.effectiveMaxHeight, overflowY: 'auto' }
					: { overflowY: 'hidden' }),
				...this.viewportWidthStyle
			}
		},
		headerStyle () {
			return {
				top: this.stickyTopCss,
				...this.viewportWidthStyle
			}
		}
	},
	watch: {
		showStickyScrollbar (value) {
			if (!value) return
			this.$nextTick(() => {
				const proxy = this.$refs.scrollbarProxy
				const inner = this.$refs.tableInner
				if (proxy) this.overlayScrollbar = proxy.offsetHeight === proxy.clientHeight
				if (proxy && inner && Math.abs(proxy.scrollLeft - inner.scrollLeft) > 0.5) {
					proxy.scrollLeft = inner.scrollLeft
				}
			})
		}
	},
	mounted () {
		this.inScrollContainer = this.hasScrollAncestor()
		this.measure()
		this.updateEdgeState()
		if (typeof ResizeObserver !== 'undefined' && this.$refs.tableInner) {
			this.$_scrollResizeObserver = new ResizeObserver(this.scheduleMeasure)
			this.observeInnerContent()
		}
	},
	updated () {
		this.observeInnerContent()
		this.$nextTick(this.measure)
	},
	beforeDestroy () {
		if (this.$_scrollResizeObserver) {
			this.$_scrollResizeObserver.disconnect()
			this.$_scrollResizeObserver = null
		}
		if (this.$_scrollReleaseTimer) {
			clearTimeout(this.$_scrollReleaseTimer)
			this.$_scrollReleaseTimer = null
		}
		if (this.$_edgeRafId) {
			cancelAnimationFrame(this.$_edgeRafId)
			this.$_edgeRafId = null
		}
		if (this.$_measureRafId) {
			cancelAnimationFrame(this.$_measureRafId)
			this.$_measureRafId = null
		}
	},
	created () {
		this.$_cacheStore = {
			widthBySignature: Object.create(null),
			widthOrder: [],
			normalizedByTransition: Object.create(null),
			normalizedOrder: [],
			maxEntries: 120
		}
		const handlers = new Set()
		this.$_rowEventBus = {
			$on (_event, fn) { handlers.add(fn) },
			$off (_event, fn) { handlers.delete(fn) },
			$emit (_event, ...args) { handlers.forEach(fn => fn(...args)) }
		}
	},
	provide () {
		const self = this
		return {
			baseTableStickyHeader: this.stickyHeader,
			baseTableNested: true,
			get baseTableWidthCacheStore () { return self.$_cacheStore },
			get baseTableRowEventBus () { return self.$_rowEventBus }
		}
	},
	methods: {
		slotParts () {
			const vnodes = this.$slots.default || []
			if (this.$_slotParts && this.$_slotParts.source === vnodes && this.$_slotParts.sticky === this.isPageStickyHeader) {
				return this.$_slotParts
			}
			const index = this.isPageStickyHeader ? vnodes.findIndex(isHeaderRowVNode) : -1
			this.$_slotParts = {
				source: vnodes,
				sticky: this.isPageStickyHeader,
				before: index > -1 ? vnodes.slice(0, index) : [],
				header: index > -1 ? vnodes[index] : null,
				body: index > -1 ? vnodes.slice(index + 1) : vnodes
			}
			return this.$_slotParts
		},
		hasScrollAncestor () {
			let ancestor = this.$el.parentElement
			while (ancestor && ancestor !== document.body && ancestor !== document.documentElement) {
				const { overflowY } = window.getComputedStyle(ancestor)
				if (['auto', 'scroll', 'hidden', 'overlay'].includes(overflowY)) return true
				ancestor = ancestor.parentElement
			}
			return false
		},
		// Layout reads must not run synchronously inside a ResizeObserver
		// callback — WebKit crashes (SIGSEGV in style resolution) when layout
		// is forced re-entrantly during observation delivery on iOS.
		scheduleMeasure () {
			if (this.$_measureRafId) return
			this.$_measureRafId = requestAnimationFrame(() => {
				this.$_measureRafId = null
				if (this._isDestroyed || this._isBeingDestroyed) return
				this.measure()
			})
		},
		measure () {
			const el = this.$refs.tableInner
			if (!el) return
			if (this.viewportWidth !== el.clientWidth) {
				this.viewportWidth = el.clientWidth
			}
			if (this.contentWidth !== el.scrollWidth) {
				this.contentWidth = el.scrollWidth
			}
			const hasOverflowX = el.scrollWidth > el.clientWidth + 1
			if (this.hasOverflowX !== hasOverflowX) {
				this.hasOverflowX = hasOverflowX
			}
			this.syncOverlayScroll()
			this.updateEdgeState()
		},
		// The overlay content can be narrower than the body while its rows are
		// still being laid out, which clamps scrollLeft — re-align it from the body.
		syncOverlayScroll () {
			const overlay = this.$refs.overlayTarget
			const inner = this.$refs.tableInner
			if (overlay && inner && Math.abs(overlay.scrollLeft - inner.scrollLeft) > 0.5) {
				overlay.scrollLeft = inner.scrollLeft
			}
		},
		onOverlayScroll () {
			// Ignore programmatic scrolls (e.g. clamped syncs) so they never drive the body.
			if (this.$_scrollSource !== 'overlay') return
			this.onScroll('overlay')
		},
		updateEdgeState () {
			const el = this.$refs.tableInner
			if (!el) return
			const scrolled = el.scrollLeft > 0
			const scrolledEnd = el.scrollLeft < el.scrollWidth - el.clientWidth - 2
			if (this.isScrolledX !== scrolled) {
				this.isScrolledX = scrolled
			}
			if (this.isScrolledXEnd !== scrolledEnd) {
				this.isScrolledXEnd = scrolledEnd
			}
		},
		scheduleEdgeUpdate () {
			if (this.$_edgeRafId) return
			this.$_edgeRafId = requestAnimationFrame(() => {
				this.$_edgeRafId = null
				if (this._isDestroyed || this._isBeingDestroyed) return
				this.updateEdgeState()
			})
		},
		observeInnerContent () {
			const observer = this.$_scrollResizeObserver
			const inner = this.$refs.tableInner
			if (!observer || !inner) return
			const overlay = this.$refs.overlayTarget
			const overlayTargets = overlay
				? Array.from(overlay.children).flatMap(child => [child, ...child.children])
				: []
			const targets = [inner, ...inner.children, ...overlayTargets]
			const observed = this.$_observedTargets || []
			if (observed.length === targets.length && observed.every((target, i) => target === targets[i])) return
			observer.disconnect()
			targets.forEach(target => observer.observe(target))
			this.$_observedTargets = targets
		},
		lockScrollSource (source) {
			this.$_scrollSource = source
			if (this.$_scrollReleaseTimer) {
				clearTimeout(this.$_scrollReleaseTimer)
			}
			this.$_scrollReleaseTimer = setTimeout(() => {
				this.$_scrollSource = null
				this.$_scrollReleaseTimer = null
			}, 120)
		},
		onScroll (source) {
			if (this.$_scrollSource && this.$_scrollSource !== source) return
			this.lockScrollSource(source)

			const inner = this.$refs.tableInner
			const refs = {
				inner: 'tableInner',
				header: 'headerTarget',
				proxy: 'scrollbarProxy',
				overlay: 'overlayTarget'
			}
			const sourceEl = this.$refs[refs[source]]
			if (!inner || !sourceEl) return
			const x = sourceEl.scrollLeft
			const targets = Object.keys(refs)
				.filter(key => key !== source)
				.map(key => this.$refs[refs[key]])
			targets.forEach((el) => {
				if (el && Math.abs(el.scrollLeft - x) > 0.5) {
					el.scrollLeft = x
				}
			})
			this.scheduleEdgeUpdate()
		},
		handlePageChange (page) {
			const inner = this.$refs.tableInner
			if (this.effectiveMaxHeight && inner) {
				inner.scrollTop = 0
			} else {
				const stickyTop = parseFloat(window.getComputedStyle(this.$el).scrollMarginTop) || 0
				if (this.$el.getBoundingClientRect().top < stickyTop) {
					this.$el.scrollIntoView({ block: 'start', behavior: 'smooth' })
				}
			}
			this.$emit('page-change', page)
		}
	}
}
</script>
<style lang="scss" module>
.app-table {
	display: flex;
	flex-flow: column;
	width: 100%;
	margin-bottom: 20px;
	box-shadow: 0 1px 3px 0 getColor(shadow-light);

	&--without-footer {
		border-bottom-left-radius: 5px;
		border-bottom-right-radius: 5px;
	}

	&__sticky-header {
		position: sticky;
		z-index: 70;
		border-top-left-radius: 5px;
		border-top-right-radius: 5px;
	}

	&__sticky-header-scroller {
		overflow: auto hidden;
		overscroll-behavior-x: none;
		scrollbar-width: none;
		border-top-left-radius: 5px;
		border-top-right-radius: 5px;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	&__sticky-overlay {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		overflow: auto hidden;
		overscroll-behavior-x: none;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	&__sticky-footer {
		position: sticky;
		bottom: 0;
		z-index: 70;
		border-bottom-left-radius: 5px;
		border-bottom-right-radius: 5px;
	}

	&__inner {
		overflow-x: auto;
		overscroll-behavior-x: none;

		&::-webkit-scrollbar {
			width: 8px;
			height: 8px;
		}

		&::-webkit-scrollbar-track {
			background: getColor(surface);
		}

		&::-webkit-scrollbar-thumb {
			background: getColor(border-secondary);
			border-radius: 4px;

			&:hover {
				background: getColor(primary);
			}
		}
	}

	&__inner--proxy-scroll {
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	&__sticky-scrollbar {
		overflow: auto hidden;
		overscroll-behavior-x: none;
		background: getColor(surface);

		&--last {
			border-bottom-left-radius: 5px;
			border-bottom-right-radius: 5px;
		}
	}
}
</style>
